import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { downloadFile, errMsg } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { PageHeader, Loading, ErrorBox } from "@/components/common/ui-bits";

const inline = (t) => t.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => (p.startsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : p.startsWith("`") ? <code key={i} className="rounded bg-slate-100 px-1 text-xs">{p.slice(1, -1)}</code> : p));

function Table({ rows }) {
  const [head, ...body] = rows;
  return (
    <div className="my-3 overflow-x-auto"><table className="w-full border-collapse text-sm">
      <thead><tr>{head.map((c, i) => <th key={i} className="border border-slate-200 bg-slate-50 px-3 py-2 text-left font-semibold">{inline(c)}</th>)}</tr></thead>
      <tbody>{body.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="border border-slate-200 px-3 py-2 align-top">{inline(c)}</td>)}</tr>)}</tbody>
    </table></div>
  );
}

function Markdown({ text }) {
  const out = [];
  let table = [];
  const flush = (k) => { if (table.length) { out.push(<Table key={`t${k}`} rows={table} />); table = []; } };
  text.split("\n").forEach((raw, k) => {
    const s = raw.trim();
    if (s.startsWith("|")) { if (!/^\|[\s:|-]+\|$/.test(s)) table.push(s.replace(/^\||\|$/g, "").split("|").map((c) => c.trim())); return; }
    flush(k);
    if (!s) return;
    if (s.startsWith("# ")) out.push(<h1 key={k} className="font-display text-3xl font-bold text-slate-900">{s.slice(2)}</h1>);
    else if (s.startsWith("## ")) out.push(<h2 key={k} className="mt-8 border-b border-slate-200 pb-2 font-display text-lg font-bold text-slate-900">{s.slice(3)}</h2>);
    else if (/^(- |\d+\. )/.test(s)) out.push(<p key={k} className="ml-4 py-0.5 text-sm text-slate-700">{s.startsWith("- ") ? "• " : `${s.split(" ")[0]} `}{inline(s.replace(/^(- |\d+\. )/, ""))}</p>);
    else out.push(<p key={k} className="mt-2 text-sm text-slate-700">{inline(s)}</p>);
  });
  flush("end");
  return <article className="card-box p-6 sm:p-10" data-testid="delivery-doc-content">{out}</article>;
}

export default function DeliveryDoc() {
  const { data, error } = useApi("/delivery-doc");
  const [busy, setBusy] = useState(false);
  const pdf = async () => { setBusy(true); try { await downloadFile("/delivery-doc/pdf", {}, "RinoTECH-Documento-de-entrega.pdf"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="max-w-5xl">
      <PageHeader title="Documento de entrega" subtitle="Módulos, pantallas, roles, usuarios de prueba, flujos probados, integraciones, limitaciones e instrucciones."
        actions={<Button onClick={pdf} disabled={busy} data-testid="delivery-doc-pdf-button"><FileText className="mr-2 h-4 w-4" />Descargar PDF</Button>} />
      <ErrorBox text={error} />
      {!data ? !error && <Loading /> : <Markdown text={data.markdown} />}
    </div>
  );
}
